import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Bot,
  Shield,
  CreditCard,
  BarChart3,
  Bell,
  Zap,
  Globe,
  CheckCircle2,
  Users,
  Building2,
  TrendingUp,
  ChevronRight,
  Star,
  Quote,
  Play,
  Wrench,
  MessageSquare,
  Plus,
  Minus,
} from "lucide-react";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/SiteFooter";
import VideoModal from "../components/VideoModal";

/* ── Stat counter hook ── */
const useCounter = (end: number, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start]);
  return count;
};

/* ── Feature card ── */
const FeatureCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  desc: string;
  accent: string;
  delay?: number;
  isAI?: boolean;
}> = ({ icon, title, desc, accent, delay = 0, isAI }) => (
  <div
    className="nx-card nx-anim-fade-up"
    style={{
      padding: "28px",
      animationDelay: `${delay}ms`,
      position: "relative",
      overflow: "hidden",
    }}
  >
    {/* Top accent line */}
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: accent,
      }}
    />

    {/* Icon */}
    <div
      style={{
        width: 48,
        height: 48,
        borderRadius: 12,
        background:
          accent.includes("1A6CF6") || accent.includes("blue")
            ? "rgba(26,108,246,0.1)"
            : accent.includes("6D28D9") || accent.includes("violet")
              ? "rgba(109,40,217,0.1)"
              : accent.includes("10B981") || accent.includes("green")
                ? "rgba(16,185,129,0.1)"
                : accent.includes("F59E0B") || accent.includes("gold")
                  ? "rgba(245,158,11,0.1)"
                  : accent.includes("06B6D4") || accent.includes("cyan")
                    ? "rgba(6,182,212,0.1)"
                    : "rgba(239,68,68,0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 16,
        color:
          accent.split(",")[0].replace(/.*\(/, "").replace(/#/, "").length > 0
            ? undefined
            : undefined,
      }}
    >
      <span style={{ color: accent.includes("gradient") ? "#1A6CF6" : accent }}>
        {icon}
      </span>
    </div>

    {isAI && (
      <span
        className="nx-badge nx-badge-blue"
        style={{ marginBottom: 10, fontSize: 10 }}
      >
        <Sparkles size={9} /> AI-Powered
      </span>
    )}

    <h3
      style={{
        fontFamily: "Syne, sans-serif",
        fontSize: 16,
        fontWeight: 700,
        color: "var(--nx-text-primary)",
        marginBottom: 8,
        letterSpacing: "-0.02em",
      }}
    >
      {title}
    </h3>
    <p
      style={{
        fontSize: 13,
        color: "var(--nx-text-secondary)",
        lineHeight: 1.65,
      }}
    >
      {desc}
    </p>
  </div>
);

/* ── Testimonial ── */
const TestimonialCard: React.FC<{
  name: string;
  role: string;
  society: string;
  text: string;
  rating: number;
}> = ({ name, role, society, text, rating }) => (
  <div className="nx-card" style={{ padding: 24 }}>
    <Quote
      size={20}
      style={{ color: "var(--nx-blue)", marginBottom: 12, opacity: 0.6 }}
    />
    <p
      style={{
        fontSize: 13,
        color: "var(--nx-text-secondary)",
        lineHeight: 1.7,
        marginBottom: 16,
      }}
    >
      "{text}"
    </p>
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-end",
      }}
    >
      <div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "var(--nx-text-primary)",
          }}
        >
          {name}
        </div>
        <div style={{ fontSize: 11, color: "var(--nx-text-muted)" }}>
          {role} · {society}
        </div>
      </div>
      <div style={{ display: "flex", gap: 2 }}>
        {Array.from({ length: rating }).map((_, i) => (
          <Star key={i} size={12} fill="#F59E0B" color="#F59E0B" />
        ))}
      </div>
    </div>
  </div>
);

const Landing: React.FC = () => {
  const statsRef = useRef<HTMLDivElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  const c1 = useCounter(27000, 2000, statsVisible);
  const c2 = useCounter(5, 2000, statsVisible);
  const c3 = useCounter(50, 2000, statsVisible);
  const c4 = useCounter(98, 2000, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setStatsVisible(true);
      },
      { threshold: 0.3 },
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const features = [
    {
      icon: <Bot size={22} />,
      title: "NexaBot AI Assistant",
      desc: "AI chatbot that handles resident queries, booking requests, and complaint escalation 24/7 — in English, Hindi, and Tamil.",
      accent: "#1A6CF6",
      isAI: true,
    },
    {
      icon: <Shield size={22} />,
      title: "AI Gate & Security",
      desc: "Anomaly detection on visitor patterns, blacklist alerts, and QR gate passes generated instantly via WhatsApp.",
      accent: "#6D28D9",
      isAI: true,
    },
    {
      icon: <CreditCard size={22} />,
      title: "Smart Billing Engine",
      desc: "GST-ready invoicing, auto-reconciliation, TDS compliance, and AI-predicted cash flow forecasting for RWAs.",
      accent: "#10B981",
    },
    {
      icon: <Wrench size={22} />,
      title: "Predictive Maintenance",
      desc: "AI analyses asset usage history to predict equipment failures before they happen — lifts, pumps, generators.",
      accent: "#F59E0B",
      isAI: true,
    },
    {
      icon: <BarChart3 size={22} />,
      title: "Sentiment Analytics",
      desc: "Analyse helpdesk tickets and poll responses to surface community mood trends for proactive committee action.",
      accent: "#06B6D4",
      isAI: true,
    },
    {
      icon: <Bell size={22} />,
      title: "Community Hub",
      desc: "Announcements, polls, events, facility booking, and classifieds — everything residents need in one elegant app.",
      accent: "#EF4444",
    },
  ];

  const premiumThemes = [
    {
      title: "Executive Suite",
      description:
        "Premium design with enterprise widgets, strict access mode, and audit-ready configuration.",
      path: "/dashboard/themes/executive-suite",
    },
    {
      title: "Modern Minimal",
      description:
        "Clean theme with lightweight navigation, elegant spacing, and secure frontend validation.",
      path: "/dashboard/themes/modern-minimal",
    },
    {
      title: "Corporate Edge",
      description:
        "Business-grade theme with role-based controls, encrypted payload flags, and audit tracking.",
      path: "/dashboard/themes/corporate-edge",
    },
  ];

  const themeQuickLinks = [
    { name: "Executive Suite", path: "/dashboard/themes/executive-suite" },
    { name: "Modern Minimal", path: "/dashboard/themes/modern-minimal" },
    { name: "Corporate Edge", path: "/dashboard/themes/corporate-edge" },
  ];

  const [selectedTheme, setSelectedTheme] = useState<{
    name: string;
    path: string;
  } | null>(null);

  const [demoOpen, setDemoOpen] = useState(false);

  useEffect(() => {
    try {
      const stored = window.localStorage.getItem("nexora-selected-theme");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.name && parsed?.path) {
          setSelectedTheme(parsed);
        }
      }
    } catch {
      // ignore malformed storage value
    }
  }, []);

  const saveThemeSelection = (theme: { name: string; path: string }) => {
    setSelectedTheme(theme);
    window.localStorage.setItem("nexora-selected-theme", JSON.stringify(theme));
  };

  const starterLink = selectedTheme?.path || "/dashboard";

  const pricingTiers = [
    {
      name: "Starter",
      price: "Free",
      period: "up to 100 units",
      description: "Everything a single community needs to get off paper and spreadsheets.",
      features: [
        "Resident & tenant directory",
        "Visitor & delivery management",
        "Maintenance billing & dues tracking",
        "Community notices & polls",
        "Mobile-friendly resident access",
      ],
      cta: "Start Free Trial",
      ctaLink: "starter",
      highlighted: false,
    },
    {
      name: "Growth",
      price: "Custom Pricing",
      period: "up to 500 units",
      description: "For growing communities that need AI automation and financial depth.",
      features: [
        "Everything in Starter",
        "NexaBot AI assistant for residents",
        "Facility booking & event management",
        "Budgets, funds & financial reports",
        "Priority support",
      ],
      cta: "Talk to Sales",
      ctaLink: "sales",
      highlighted: true,
    },
    {
      name: "Enterprise",
      price: "Custom Pricing",
      period: "unlimited units",
      description: "For managed portfolios and large townships with dedicated needs.",
      features: [
        "Everything in Growth",
        "Dedicated account manager",
        "Custom integrations & data migration",
        "Advanced security & audit logs",
        "SLA-backed uptime guarantee",
      ],
      cta: "Contact Sales",
      ctaLink: "sales",
      highlighted: false,
    },
  ];

  const testimonials = [
    {
      name: "Ramesh Nair",
      role: "RWA President",
      society: "Sobha Meridian, Bengaluru",
      text: "Nexora replaced three different tools we were using. The AI billing prediction alone saved us 2 weeks of manual reconciliation every quarter.",
      rating: 5,
    },
    {
      name: "Kavitha Suresh",
      role: "Facility Manager",
      society: "Prestige Lakeside, Chennai",
      text: "NexaBot handles 90% of resident queries before they even reach our office. It is like having a 24/7 community manager that never takes a day off.",
      rating: 5,
    },
    {
      name: "Arjun Mehta",
      role: "Committee Member",
      society: "Godrej Aria, Pune",
      text: "The visitor anomaly detection caught an unauthorized entry pattern we never would have noticed manually. The AI security layer is genuinely impressive.",
      rating: 5,
    },
  ];

  const faqs = [
    {
      q: "What is Nexora and who is it for?",
      a: "Nexora is an AI-powered society and apartment management platform for residential communities — RWAs, gated communities, and townships. It brings resident management, billing, security, facilities, and AI assistance into one platform for committees, facility managers, and residents.",
    },
    {
      q: "How does Nexora handle visitor and gate security management?",
      a: "Nexora digitises visitor entry with pre-approvals, delivery tracking, and a blacklist registry, plus AI-powered anomaly detection that flags unusual gate activity in real time for your security team.",
    },
    {
      q: "Can Nexora manage billing and maintenance dues collection?",
      a: "Yes. Nexora automates monthly maintenance billing, dues tracking, and digital payment collection, with AI-assisted cash flow forecasting so committees can predict collections with high accuracy.",
    },
    {
      q: "Does Nexora support facility booking and events?",
      a: "Residents can book shared facilities like clubhouses, courts, and halls directly through Nexora, and committees can publish and manage community events and RSVPs from the same dashboard.",
    },
    {
      q: "How does NexaBot help residents and committees?",
      a: "NexaBot is Nexora's AI assistant — it handles common resident queries like dues status and facility availability 24/7 in multiple languages, and can auto-summarise committee meeting notes into action items.",
    },
    {
      q: "Can Nexora manage multiple societies or blocks?",
      a: "Yes. Nexora supports multi-block and multi-society structures under one account, with role-based access so admins, staff, and residents only see what's relevant to them.",
    },
    {
      q: "What security and compliance measures does Nexora follow?",
      a: "Nexora uses role-based access controls, audit logs, and encrypted data handling for every community on the platform, with infrastructure built for Indian data residency requirements.",
    },
    {
      q: "Does Nexora offer a free trial for smaller communities?",
      a: "Yes — the Starter plan is free for communities up to 100 units, with no credit card required, so you can try Nexora with your own community before deciding on a paid plan.",
    },
    {
      q: "How long does it take to go live with Nexora?",
      a: "Most communities can go live within 24 hours of signing up — you can set up blocks, units, and residents yourself, or our team can help with onboarding for larger communities.",
    },
    {
      q: "Can residents and committee members access Nexora on mobile?",
      a: "Nexora is fully responsive and works on any mobile browser, so residents and committee members can manage dues, bookings, and requests from their phones without installing a separate app.",
    },
  ];

  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <div style={{ background: "var(--nx-navy)", minHeight: "100vh" }}>
      <Navbar variant="transparent" />

      {/* ══ HERO ══════════════════════════════════════ */}
      <section
        style={{
          position: "relative",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "120px 5% 80px",
          overflow: "hidden",
        }}
      >
        {/* Animated grid background */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `
            linear-gradient(rgba(26,108,246,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,108,246,0.06) 1px, transparent 1px)
          `,
            backgroundSize: "60px 60px",
          }}
        />

        {/* Radial glow */}
        <div
          style={{
            position: "absolute",
            width: "800px",
            height: "600px",
            background:
              "radial-gradient(ellipse at 50% 40%, rgba(26,108,246,0.18) 0%, rgba(109,40,217,0.08) 40%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
          }}
        />

        {/* Floating orbs */}
        <div
          className="nx-anim-float"
          style={{
            position: "absolute",
            top: "20%",
            left: "8%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(26,108,246,0.08) 0%, transparent 70%)",
            animationDuration: "6s",
          }}
        />
        <div
          className="nx-anim-float"
          style={{
            position: "absolute",
            bottom: "15%",
            right: "6%",
            width: 250,
            height: 250,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(109,40,217,0.08) 0%, transparent 70%)",
            animationDuration: "8s",
            animationDelay: "1s",
          }}
        />

        {/* Content */}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            maxWidth: 800,
          }}
        >
          {/* Top badge */}
          <div className="nx-anim-fade-up" style={{ marginBottom: 24 }}>
            <span
              className="nx-badge nx-badge-dark"
              style={{ padding: "6px 16px", fontSize: 12 }}
            >
              <Sparkles size={12} style={{ color: "#60A5FA" }} />
              AI-Powered Society Management · Built for India & Beyond
            </span>
          </div>

          {/* Headline */}
          <h1
            className="nx-anim-fade-up nx-delay-1"
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: "clamp(40px, 6vw, 72px)",
              fontWeight: 800,
              color: "#fff",
              letterSpacing: "-0.04em",
              lineHeight: 1.08,
              marginBottom: 12,
            }}
          >
            The Next Era of
          </h1>
          <h1
            className="nx-anim-fade-up nx-delay-2"
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: "clamp(40px, 6vw, 72px)",
              fontWeight: 800,
              letterSpacing: "-0.04em",
              lineHeight: 1.08,
              marginBottom: 28,
            }}
          >
            <span className="nx-shimmer-text">Community Living</span>
          </h1>

          {/* Sub */}
          <p
            className="nx-anim-fade-up nx-delay-3"
            style={{
              fontSize: "clamp(15px, 2vw, 18px)",
              color: "rgba(255,255,255,0.5)",
              lineHeight: 1.7,
              maxWidth: 580,
              margin: "0 auto 40px",
              fontWeight: 300,
            }}
          >
            Nexora brings AI-powered intelligence to every corner of your gated
            community — from gate security to billing, maintenance to resident
            happiness.
          </p>

          {/* CTAs */}
          <div
            className="nx-anim-fade-up nx-delay-4"
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              flexWrap: "wrap",
            }}
          >
            <Link to={starterLink} className="nx-btn nx-btn-primary nx-btn-lg">
              Start Free Trial
              <ArrowRight size={16} />
            </Link>
            <button
              className="nx-btn nx-btn-ghost-dark nx-btn-lg"
              onClick={() => setDemoOpen(true)}
            >
              <Play size={14} />
              Watch Demo
            </button>
          </div>

          <div
            className="nx-anim-fade-up nx-delay-5"
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 14,
              alignItems: "center",
              marginTop: 34,
            }}
          >
            <div style={{ fontSize: 14, color: "rgba(255,255,255,0.72)" }}>
              Switch to a premium theme from the landing page.
            </div>
            {selectedTheme && (
              <div style={{ color: "#fff", fontSize: 13 }}>
                Last selected theme: <strong>{selectedTheme.name}</strong>
              </div>
            )}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                justifyContent: "center",
              }}
            >
              {themeQuickLinks.map((theme) => (
                <Link
                  key={theme.path}
                  to={theme.path}
                  className="nx-btn nx-btn-outline"
                  style={{ color: "#fff" }}
                  onClick={() => saveThemeSelection(theme)}
                >
                  {theme.name}
                </Link>
              ))}
              {selectedTheme && (
                <Link
                  to={selectedTheme.path}
                  className="nx-btn nx-btn-primary"
                  style={{ minWidth: 200 }}
                  onClick={() => saveThemeSelection(selectedTheme)}
                >
                  Continue {selectedTheme.name}
                </Link>
              )}
            </div>
          </div>

          {/* Trust badges */}
          <div
            className="nx-anim-fade-up nx-delay-5"
            style={{
              display: "flex",
              gap: 24,
              justifyContent: "center",
              marginTop: 40,
              flexWrap: "wrap",
            }}
          >
            {[
              {
                icon: <CheckCircle2 size={13} />,
                text: "No credit card required",
              },
              {
                icon: <CheckCircle2 size={13} />,
                text: "Free up to 100 units",
              },
              { icon: <CheckCircle2 size={13} />, text: "Live in 24 hours" },
            ].map((item) => (
              <div
                key={item.text}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "rgba(255,255,255,0.4)",
                }}
              >
                <span style={{ color: "var(--nx-green)" }}>{item.icon}</span>
                {item.text}
              </div>
            ))}
          </div>
        </div>

        {/* Dashboard preview */}
        <div
          className="nx-anim-fade-up nx-delay-6"
          style={{
            position: "relative",
            zIndex: 2,
            marginTop: 64,
            width: "100%",
            maxWidth: 900,
          }}
        >
          <div
            style={{
              background: "var(--nx-navy2)",
              borderRadius: 20,
              border: "1px solid rgba(255,255,255,0.08)",
              boxShadow:
                "0 40px 120px rgba(0,0,0,0.5), 0 0 0 1px rgba(26,108,246,0.1)",
              overflow: "hidden",
            }}
          >
            {/* Browser chrome */}
            <div
              style={{
                padding: "12px 16px",
                background: "var(--nx-navy3)",
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", gap: 6 }}>
                {["#EF4444", "#F59E0B", "#10B981"].map((c) => (
                  <div
                    key={c}
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: c,
                      opacity: 0.7,
                    }}
                  />
                ))}
              </div>
              <div
                style={{
                  flex: 1,
                  maxWidth: 300,
                  margin: "0 auto",
                  background: "rgba(255,255,255,0.05)",
                  borderRadius: 6,
                  padding: "4px 12px",
                  fontSize: 11,
                  color: "rgba(255,255,255,0.25)",
                  textAlign: "center",
                }}
              >
                nexora.pentagonanalytics.io/dashboard
              </div>
            </div>

            {/* Mini dashboard preview */}
            <div style={{ padding: 20 }}>
              {/* Stat row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4,1fr)",
                  gap: 12,
                  marginBottom: 16,
                }}
              >
                {[
                  {
                    label: "Total Units",
                    value: "1,248",
                    change: "+12",
                    color: "#1A6CF6",
                  },
                  {
                    label: "Dues Collected",
                    value: "₹4.2L",
                    change: "94%",
                    color: "#10B981",
                  },
                  {
                    label: "Visitors Today",
                    value: "86",
                    change: "4 pending",
                    color: "#6D28D9",
                  },
                  {
                    label: "Open Tickets",
                    value: "23",
                    change: "5 resolved",
                    color: "#F59E0B",
                  },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    style={{
                      background: "var(--nx-navy3)",
                      borderRadius: 10,
                      padding: "12px 14px",
                      border: "1px solid rgba(255,255,255,0.05)",
                      borderTop: `2px solid ${stat.color}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 18,
                        fontWeight: 700,
                        color: "#fff",
                        fontFamily: "Syne, sans-serif",
                      }}
                    >
                      {stat.value}
                    </div>
                    <div
                      style={{
                        fontSize: 10,
                        color: "rgba(255,255,255,0.35)",
                        marginTop: 2,
                      }}
                    >
                      {stat.label}
                    </div>
                    <div
                      style={{ fontSize: 10, color: stat.color, marginTop: 4 }}
                    >
                      ↑ {stat.change}
                    </div>
                  </div>
                ))}
              </div>

              {/* AI insight banner */}
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(26,108,246,0.15), rgba(109,40,217,0.1))",
                  border: "1px solid rgba(26,108,246,0.2)",
                  borderRadius: 10,
                  padding: "12px 16px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Sparkles
                  size={16}
                  style={{ color: "#60A5FA", flexShrink: 0 }}
                />
                <div style={{ fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
                  <strong style={{ color: "#60A5FA" }}>NexaBot:</strong> Gate B
                  traffic is 40% above Tuesday average — 3 unverified entries
                  detected. Recommend enhanced check-in.
                </div>
                <button
                  style={{
                    marginLeft: "auto",
                    padding: "4px 12px",
                    background: "rgba(26,108,246,0.2)",
                    border: "1px solid rgba(26,108,246,0.3)",
                    borderRadius: 6,
                    color: "#60A5FA",
                    fontSize: 11,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  Review
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ STATS ══════════════════════════════════════ */}
      <section
        ref={statsRef}
        style={{
          background: "var(--nx-navy2)",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          borderBottom: "1px solid rgba(255,255,255,0.05)",
          padding: "60px 5%",
        }}
      >
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(4,1fr)",
            gap: 40,
            textAlign: "center",
          }}
        >
          {[
            {
              value: c1.toLocaleString(),
              suffix: "+",
              label: "Communities",
              icon: <Building2 size={20} />,
            },
            {
              value: c2,
              suffix: "M+",
              label: "Homes",
              icon: <Users size={20} />,
            },
            {
              value: c3,
              suffix: "+",
              label: "Cities",
              icon: <Globe size={20} />,
            },
            {
              value: c4,
              suffix: "%",
              label: "Satisfaction",
              icon: <TrendingUp size={20} />,
            },
          ].map((stat, i) => (
            <div
              key={stat.label}
              className="nx-anim-fade-up"
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div
                style={{
                  color: "var(--nx-blue-light)",
                  marginBottom: 8,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {stat.icon}
              </div>
              <div
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: 42,
                  fontWeight: 800,
                  color: "#fff",
                  letterSpacing: "-0.04em",
                  lineHeight: 1,
                }}
              >
                {stat.value}
                <span style={{ color: "var(--nx-blue)" }}>{stat.suffix}</span>
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "rgba(255,255,255,0.4)",
                  marginTop: 6,
                }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ══ FEATURES ══════════════════════════════════ */}
      <section
        id="features"
        style={{ padding: "100px 5%", background: "var(--nx-surface-2)" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          {/* Header */}
          <div
            className="nx-anim-fade-up"
            style={{ textAlign: "center", marginBottom: 60 }}
          >
            <span
              className="nx-badge nx-badge-blue"
              style={{ marginBottom: 16 }}
            >
              <Zap size={11} /> Platform Features
            </span>
            <h2
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: "clamp(28px, 4vw, 44px)",
                color: "var(--nx-text-primary)",
                marginBottom: 16,
                letterSpacing: "-0.03em",
              }}
            >
              Everything your community needs,
              <br />
              <span style={{ color: "var(--nx-blue)" }}>
                intelligently connected
              </span>
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "var(--nx-text-secondary)",
                maxWidth: 540,
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              250+ features across security, finance, operations, and AI —
              designed for India's gated communities and built to scale
              globally.
            </p>
          </div>

          {/* Feature grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
            }}
          >
            {features.map((f, i) => (
              <FeatureCard key={f.title} {...f} delay={i * 80} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ PREMIUM THEMES ══════════════════════════════════ */}
      <section
        id="themes"
        style={{ padding: "100px 5%", background: "var(--nx-surface-1)" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            className="nx-anim-fade-up"
            style={{ textAlign: "center", marginBottom: 48 }}
          >
            <span
              className="nx-badge nx-badge-gold"
              style={{ marginBottom: 16 }}
            >
              <Sparkles size={11} /> Premium Themes
            </span>
            <h2
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: "clamp(28px, 4vw, 44px)",
                color: "var(--nx-text-primary)",
                marginBottom: 16,
                letterSpacing: "-0.03em",
              }}
            >
              Choose a premium theme with secure configuration,
              <br /> purpose-built for community operations.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: "var(--nx-text-secondary)",
                maxWidth: 540,
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              Each premium theme page includes the fields needed to define
              style, access, and audit-ready backend payloads.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
            }}
          >
            {premiumThemes.map((theme) => (
              <div
                key={theme.title}
                className="nx-card nx-anim-fade-up"
                style={{
                  padding: 28,
                  minHeight: 280,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 16,
                    }}
                  >
                    <Sparkles size={18} style={{ color: "var(--nx-gold)" }} />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: "var(--nx-gold)",
                      }}
                    >
                      Premium
                    </span>
                  </div>
                  <h3
                    style={{
                      fontSize: 20,
                      marginBottom: 12,
                      color: "var(--nx-text-primary)",
                    }}
                  >
                    {theme.title}
                  </h3>
                  <p
                    style={{
                      fontSize: 14,
                      color: "var(--nx-text-secondary)",
                      lineHeight: 1.8,
                    }}
                  >
                    {theme.description}
                  </p>
                </div>
                <Link
                  to={theme.path}
                  className="nx-btn nx-btn-primary"
                  style={{ marginTop: 24, alignSelf: "flex-start" }}
                >
                  Open theme page
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ AI SECTION ══════════════════════════════════ */}
      <section
        id="ai"
        style={{
          padding: "100px 5%",
          background: "var(--nx-navy)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
            linear-gradient(rgba(26,108,246,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(26,108,246,0.04) 1px, transparent 1px)
          `,
            backgroundSize: "60px 60px",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 1100,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 60,
              alignItems: "center",
            }}
          >
            {/* Left */}
            <div className="nx-anim-fade-up">
              <span
                className="nx-badge nx-badge-blue"
                style={{ marginBottom: 20 }}
              >
                <Bot size={11} /> Nexora AI Engine
              </span>
              <h2
                style={{
                  fontFamily: "Syne, sans-serif",
                  fontSize: "clamp(28px, 3.5vw, 44px)",
                  fontWeight: 800,
                  color: "#fff",
                  letterSpacing: "-0.03em",
                  marginBottom: 20,
                  lineHeight: 1.1,
                }}
              >
                AI that runs your
                <br />
                <span className="nx-shimmer-text">community for you</span>
              </h2>
              <p
                style={{
                  fontSize: 15,
                  color: "rgba(255,255,255,0.5)",
                  lineHeight: 1.75,
                  marginBottom: 32,
                }}
              >
                NexaBot handles 90% of resident queries before they reach your
                office. From paying dues to booking facilities — all through a
                simple chat interface available 24/7 in multiple languages.
              </p>

              <div
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                {[
                  {
                    title: "Conversational resident support",
                    desc: "English, Hindi, Tamil support out of the box",
                  },
                  {
                    title: "Anomaly detection & security alerts",
                    desc: "Real-time flagging of suspicious gate activity",
                  },
                  {
                    title: "Predictive cash flow forecasting",
                    desc: "97% accuracy on monthly dues prediction",
                  },
                  {
                    title: "AI meeting summarizer",
                    desc: "Auto-generate action items from committee meetings",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        background: "rgba(26,108,246,0.2)",
                        border: "1px solid rgba(26,108,246,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        marginTop: 2,
                      }}
                    >
                      <CheckCircle2
                        size={11}
                        style={{ color: "var(--nx-blue-light)" }}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#fff",
                          marginBottom: 2,
                        }}
                      >
                        {item.title}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: "rgba(255,255,255,0.35)",
                        }}
                      >
                        {item.desc}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Chat demo */}
            <div className="nx-anim-scale-in nx-delay-2">
              <div
                style={{
                  background: "var(--nx-navy2)",
                  borderRadius: 20,
                  border: "1px solid rgba(255,255,255,0.08)",
                  overflow: "hidden",
                  boxShadow:
                    "0 40px 100px rgba(0,0,0,0.4), 0 0 0 1px rgba(26,108,246,0.1)",
                }}
              >
                {/* Chat header */}
                <div
                  style={{
                    padding: "14px 18px",
                    background: "var(--nx-navy3)",
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #1A6CF6, #6D28D9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 16,
                    }}
                  >
                    🤖
                  </div>
                  <div>
                    <div
                      style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}
                    >
                      NexaBot
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        fontSize: 10,
                        color: "rgba(255,255,255,0.35)",
                      }}
                    >
                      <span
                        className="nx-live-dot"
                        style={{ width: 6, height: 6 }}
                      />{" "}
                      Online · AI-Powered
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div
                  style={{
                    padding: "20px 16px",
                    display: "flex",
                    flexDirection: "column",
                    gap: 14,
                  }}
                >
                  {[
                    {
                      from: "bot",
                      text: "Hello Suresh! 👋 How can I help you today? Ask me about dues, visitors, bookings, or complaints.",
                    },
                    { from: "user", text: "What is my maintenance due?" },
                    {
                      from: "bot",
                      text: "Your December dues are ₹3,500 — due by 10 Dec. Includes maintenance ₹2,800 + water ₹400 + sinking fund ₹300. Pay now?",
                    },
                    { from: "user", text: "Yes, pay now" },
                    {
                      from: "bot",
                      text: "✅ Payment of ₹3,500 initiated via your saved UPI ID. You'll receive a receipt on WhatsApp shortly.",
                    },
                  ].map((msg, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent:
                          msg.from === "user" ? "flex-end" : "flex-start",
                      }}
                    >
                      <div
                        style={{
                          maxWidth: "78%",
                          padding: "10px 14px",
                          borderRadius:
                            msg.from === "bot"
                              ? "4px 14px 14px 14px"
                              : "14px 4px 14px 14px",
                          background:
                            msg.from === "bot"
                              ? "rgba(255,255,255,0.06)"
                              : "linear-gradient(135deg, #1A6CF6, #3B82F6)",
                          color: "#fff",
                          fontSize: 12,
                          lineHeight: 1.6,
                          border:
                            msg.from === "bot"
                              ? "1px solid rgba(255,255,255,0.06)"
                              : "none",
                        }}
                      >
                        {msg.text}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Input */}
                <div
                  style={{
                    padding: "10px 14px",
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                    display: "flex",
                    gap: 8,
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      padding: "8px 14px",
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: 8,
                      border: "1px solid rgba(255,255,255,0.07)",
                      fontSize: 12,
                      color: "rgba(255,255,255,0.25)",
                    }}
                  >
                    Type a message...
                  </div>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      background: "linear-gradient(135deg, #1A6CF6, #6D28D9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                    }}
                  >
                    <ArrowRight size={14} color="#fff" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ══ PRICING ══════════════════════════════════ */}
      <section
        id="pricing"
        style={{ padding: "100px 5%", background: "var(--nx-surface-1)" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            className="nx-anim-fade-up"
            style={{ textAlign: "center", marginBottom: 50 }}
          >
            <span
              className="nx-badge nx-badge-blue"
              style={{ marginBottom: 14 }}
            >
              <Zap size={11} /> Simple Pricing
            </span>
            <h2
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: "clamp(28px, 3.5vw, 44px)",
                fontWeight: 800,
                color: "var(--nx-text-primary)",
                letterSpacing: "-0.03em",
                marginBottom: 16,
              }}
            >
              Pricing that grows with your community
            </h2>
            <p
              style={{
                fontSize: 15,
                color: "var(--nx-text-secondary)",
                maxWidth: 540,
                margin: "0 auto",
                lineHeight: 1.7,
              }}
            >
              Start free for smaller communities. Upgrade when you need AI
              automation and dedicated support.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
              alignItems: "stretch",
            }}
          >
            {pricingTiers.map((tier, i) => (
              <div
                key={tier.name}
                className="nx-card nx-anim-fade-up"
                style={{
                  padding: 28,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  animationDelay: `${i * 120}ms`,
                  border: tier.highlighted
                    ? "1px solid rgba(26,108,246,0.4)"
                    : undefined,
                  boxShadow: tier.highlighted
                    ? "0 20px 60px rgba(26,108,246,0.15)"
                    : undefined,
                  position: "relative",
                }}
              >
                {tier.highlighted && (
                  <span
                    className="nx-badge nx-badge-blue"
                    style={{
                      position: "absolute",
                      top: -12,
                      left: 28,
                      fontSize: 10,
                    }}
                  >
                    Most Popular
                  </span>
                )}
                <div>
                  <h3
                    style={{
                      fontFamily: "Syne, sans-serif",
                      fontSize: 20,
                      fontWeight: 700,
                      color: "var(--nx-text-primary)",
                      marginBottom: 8,
                    }}
                  >
                    {tier.name}
                  </h3>
                  <div style={{ marginBottom: 6 }}>
                    <span
                      style={{
                        fontFamily: "Syne, sans-serif",
                        fontSize: 28,
                        fontWeight: 800,
                        color: "var(--nx-text-primary)",
                      }}
                    >
                      {tier.price}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--nx-text-muted)",
                      marginBottom: 16,
                    }}
                  >
                    {tier.period}
                  </div>
                  <p
                    style={{
                      fontSize: 13,
                      color: "var(--nx-text-secondary)",
                      lineHeight: 1.6,
                      marginBottom: 20,
                    }}
                  >
                    {tier.description}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 10,
                      marginBottom: 24,
                    }}
                  >
                    {tier.features.map((f) => (
                      <div
                        key={f}
                        style={{ display: "flex", gap: 8, alignItems: "flex-start" }}
                      >
                        <CheckCircle2
                          size={14}
                          style={{
                            color: "var(--nx-blue)",
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                        />
                        <span
                          style={{ fontSize: 13, color: "var(--nx-text-secondary)" }}
                        >
                          {f}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <Link
                  to={tier.ctaLink === "starter" ? starterLink : "/contact"}
                  className={
                    tier.highlighted ? "nx-btn nx-btn-primary" : "nx-btn nx-btn-outline"
                  }
                  style={{ justifyContent: "center" }}
                >
                  {tier.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ ABOUT ══════════════════════════════════════ */}
      <section
        id="about"
        style={{
          padding: "100px 5%",
          background: "var(--nx-navy)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "600px",
            height: "400px",
            background:
              "radial-gradient(ellipse, rgba(109,40,217,0.15) 0%, transparent 70%)",
            top: "0%",
            right: "0%",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            maxWidth: 900,
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <span className="nx-badge nx-badge-violet" style={{ marginBottom: 20 }}>
            <Building2 size={11} /> About Nexora
          </span>
          <h2
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: "clamp(28px, 3.5vw, 44px)",
              fontWeight: 800,
              color: "#fff",
              letterSpacing: "-0.03em",
              marginBottom: 20,
              lineHeight: 1.15,
            }}
          >
            Built by Pentagon Analytics for the way
            <br />
            <span className="nx-shimmer-text">Indian communities actually run</span>
          </h2>
          <p
            style={{
              fontSize: 15,
              color: "rgba(255,255,255,0.55)",
              lineHeight: 1.8,
              maxWidth: 680,
              margin: "0 auto 40px",
            }}
          >
            Nexora is Pentagon Analytics' AI-powered society and apartment
            management platform. We started Nexora after seeing residential
            communities across India run on WhatsApp groups, paper registers,
            and disconnected billing tools. Nexora brings resident management,
            billing, security, facilities, and AI assistance into a single
            platform, purpose-built for how Indian societies and townships
            actually operate.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 20,
              textAlign: "left",
            }}
          >
            <div
              className="nx-card-dark"
              style={{ padding: 24, background: "rgba(255,255,255,0.03)" }}
            >
              <Shield size={20} style={{ color: "var(--nx-blue-light)", marginBottom: 10 }} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
                Security-first
              </h3>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
                Role-based access, audit logs, and encrypted data handling for
                every community on the platform.
              </p>
            </div>
            <div
              className="nx-card-dark"
              style={{ padding: 24, background: "rgba(255,255,255,0.03)" }}
            >
              <Globe size={20} style={{ color: "var(--nx-blue-light)", marginBottom: 10 }} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
                Built for India
              </h3>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
                Multi-language resident support, UPI-ready billing, and
                workflows designed around Indian RWAs and townships.
              </p>
            </div>
            <div
              className="nx-card-dark"
              style={{ padding: 24, background: "rgba(255,255,255,0.03)" }}
            >
              <Bot size={20} style={{ color: "var(--nx-blue-light)", marginBottom: 10 }} />
              <h3 style={{ fontSize: 15, fontWeight: 700, color: "#fff", marginBottom: 6 }}>
                AI-driven operations
              </h3>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", lineHeight: 1.6 }}>
                NexaBot and predictive insights reduce manual work for
                committees and facility staff.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ══ TESTIMONIALS ══════════════════════════════ */}
      <section
        style={{ padding: "100px 5%", background: "var(--nx-surface-2)" }}
      >
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div
            className="nx-anim-fade-up"
            style={{ textAlign: "center", marginBottom: 50 }}
          >
            <span
              className="nx-badge nx-badge-green"
              style={{ marginBottom: 14 }}
            >
              <Star size={11} /> Trusted Communities
            </span>
            <h2
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: "clamp(26px, 3.5vw, 40px)",
                color: "var(--nx-text-primary)",
                letterSpacing: "-0.03em",
              }}
            >
              Communities that made the switch
            </h2>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3,1fr)",
              gap: 20,
            }}
          >
            {testimonials.map((t, i) => (
              <div
                key={t.name}
                className="nx-anim-fade-up"
                style={{ animationDelay: `${i * 120}ms` }}
              >
                <TestimonialCard {...t} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FAQ ══════════════════════════════════════ */}
      <section style={{ padding: "100px 5%", background: "var(--nx-surface-1)" }}>
        <div style={{ maxWidth: 800, margin: "0 auto" }}>
          <div className="nx-anim-fade-up" style={{ marginBottom: 40 }}>
            <h2
              style={{
                fontFamily: "Syne, sans-serif",
                fontSize: "clamp(26px, 3.5vw, 40px)",
                fontWeight: 800,
                color: "var(--nx-text-primary)",
                letterSpacing: "-0.03em",
              }}
            >
              FAQs on Nexora
            </h2>
          </div>

          <div>
            {faqs.map((item, i) => {
              const open = openFaq === i;
              return (
                <div
                  key={item.q}
                  style={{ borderBottom: "1px solid var(--nx-border)" }}
                >
                  <button
                    onClick={() => setOpenFaq(open ? null : i)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      padding: "20px 0",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      textAlign: "left",
                    }}
                    aria-expanded={open}
                  >
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: "var(--nx-text-primary)",
                      }}
                    >
                      {item.q}
                    </span>
                    {open ? (
                      <Minus size={16} style={{ color: "var(--nx-blue)", flexShrink: 0 }} />
                    ) : (
                      <Plus size={16} style={{ color: "var(--nx-text-muted)", flexShrink: 0 }} />
                    )}
                  </button>
                  {open && (
                    <p
                      style={{
                        fontSize: 14,
                        color: "var(--nx-text-secondary)",
                        lineHeight: 1.75,
                        margin: "0 0 20px",
                        paddingRight: 32,
                      }}
                    >
                      {item.a}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══ CTA ══════════════════════════════════════ */}
      <section
        style={{
          padding: "100px 5%",
          background: "var(--nx-navy)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "600px",
            height: "400px",
            background:
              "radial-gradient(ellipse, rgba(26,108,246,0.2) 0%, transparent 70%)",
            top: "50%",
            left: "50%",
            transform: "translate(-50%,-50%)",
          }}
        />
        <div
          style={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            maxWidth: 600,
            margin: "0 auto",
          }}
        >
          <span className="nx-badge nx-badge-dark" style={{ marginBottom: 24 }}>
            <Zap size={11} style={{ color: "#F59E0B" }} /> Get started today
          </span>
          <h2
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: "clamp(30px, 4.5vw, 52px)",
              fontWeight: 800,
              color: "#fff",
              letterSpacing: "-0.04em",
              lineHeight: 1.1,
              marginBottom: 20,
            }}
          >
            Ready to bring Nexora
            <br />
            <span className="nx-shimmer-text">to your community?</span>
          </h2>
          <p
            style={{
              fontSize: 16,
              color: "rgba(255,255,255,0.45)",
              lineHeight: 1.7,
              marginBottom: 36,
            }}
          >
            Free up to 100 units. No credit card. Go live in 24 hours.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center" }}>
            <Link to={starterLink} className="nx-btn nx-btn-primary nx-btn-lg">
              Start Free Trial
              <ArrowRight size={16} />
            </Link>
            <Link to="/request-access" className="nx-btn nx-btn-ghost-dark nx-btn-lg">
              <MessageSquare size={15} />
              Talk to Sales
            </Link>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══════════════════════════════════ */}
      <SiteFooter />

      <VideoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        src="/videos/nexora-product-demo.mp4"
        title="Nexora Product Demo"
      />
    </div>
  );
};

export default Landing;
